// Measurement-based brand similarity (derived from real size_chart data).
//
// Complements the curated map in brandSimilarity.ts (Build 3b). This module
// reads aggregates from `brand_measurement_aggregates` (populated nightly
// by the `refresh-brand-aggregates` edge function) and finds brands whose
// average measurements are within a tolerance of a target brand.
//
// IMPORTANT: This file MUST stay byte-identical to
// `supabase/functions/_shared/brandSimilarityMeasured.ts`. Edge functions
// cannot import from `src/lib`, so the module is duplicated.

export interface BrandAggregateRow {
  brand: string;
  category: string;
  gender: string;
  size_key: string;
  chest_avg: number | null;
  waist_avg: number | null;
  hips_avg: number | null;
  length_avg: number | null;
  sample_size: number;
}

export interface MeasurementSimilarityResult {
  similar_brands: Array<{
    brand: string;
    distance_avg: number; // average cm difference across dimensions/sizes
    sample_overlap: number; // how many size keys both brands share
    confidence: "low" | "medium" | "high";
  }>;
}

const SIMILARITY_THRESHOLD_CM = 2; // average difference per dimension
const MIN_SHARED_SIZES = 2;
const MIN_SHARED_DIMENSIONS = 2;

/**
 * Given a target brand, finds other brands with measurement profiles
 * within the similarity threshold. Pass all aggregates in (queried separately
 * via loadBrandAggregates) so consumers can batch.
 */
export function findMeasurementSimilarBrands(
  target_brand: string,
  category: "tops",
  gender: "women" | "men" | "non-binary",
  all_aggregates: BrandAggregateRow[],
): MeasurementSimilarityResult {
  const lookup_gender: "women" | "men" = gender === "non-binary" ? "women" : gender;

  const target_rows = all_aggregates.filter(
    (r) => r.brand === target_brand && r.category === category && r.gender === lookup_gender,
  );

  if (target_rows.length === 0) {
    return { similar_brands: [] };
  }

  const candidates = new Set<string>();
  for (const row of all_aggregates) {
    if (
      row.brand !== target_brand &&
      row.category === category &&
      row.gender === lookup_gender
    ) {
      candidates.add(row.brand);
    }
  }

  const similar: MeasurementSimilarityResult["similar_brands"] = [];

  for (const candidate of candidates) {
    const candidate_rows = all_aggregates.filter(
      (r) => r.brand === candidate && r.category === category && r.gender === lookup_gender,
    );

    const target_sizes = new Set(target_rows.map((r) => r.size_key));
    const shared = candidate_rows.filter((r) => target_sizes.has(r.size_key));

    if (shared.length < MIN_SHARED_SIZES) continue;

    let total_distance = 0;
    let comparison_count = 0;
    let dimensions_compared_min = Infinity;

    for (const c_row of shared) {
      const t_row = target_rows.find((r) => r.size_key === c_row.size_key)!;
      const dims = ["chest_avg", "waist_avg", "hips_avg", "length_avg"] as const;
      let dim_distance = 0;
      let dim_count = 0;

      for (const dim of dims) {
        if (t_row[dim] != null && c_row[dim] != null) {
          dim_distance += Math.abs((t_row[dim] as number) - (c_row[dim] as number));
          dim_count += 1;
        }
      }

      if (dim_count < MIN_SHARED_DIMENSIONS) continue;

      total_distance += dim_distance / dim_count;
      comparison_count += 1;
      dimensions_compared_min = Math.min(dimensions_compared_min, dim_count);
    }

    if (comparison_count === 0) continue;

    const avg_distance = total_distance / comparison_count;

    if (avg_distance <= SIMILARITY_THRESHOLD_CM) {
      let confidence: "low" | "medium" | "high" = "low";
      if (comparison_count >= 3 && dimensions_compared_min >= 3) {
        confidence = "high";
      } else if (comparison_count >= 2 && dimensions_compared_min >= 2) {
        confidence = "medium";
      }

      similar.push({
        brand: candidate,
        distance_avg: Math.round(avg_distance * 10) / 10,
        sample_overlap: comparison_count,
        confidence,
      });
    }
  }

  similar.sort((a, b) => a.distance_avg - b.distance_avg);

  return { similar_brands: similar };
}

/**
 * Query helper. Loads all aggregates for a category from Supabase.
 * Pass any supabase-js compatible client (browser or admin).
 */
// deno-lint-ignore no-explicit-any
export async function loadBrandAggregates(
  // deno-lint-ignore no-explicit-any
  supabase: any,
  category: "tops" = "tops",
): Promise<BrandAggregateRow[]> {
  const { data, error } = await supabase
    .from("brand_measurement_aggregates")
    .select("*")
    .eq("category", category);

  if (error) {
    console.warn("[brand-aggregates] load failed:", error);
    return [];
  }

  return (data ?? []) as BrandAggregateRow[];
}
